import { defineSlotRecipe } from '@chakra-ui/react';

import { focusRing } from '../focusRing';

/*
 * Design system source: .claude/skills/monetizenow-design/preview/toggles.html
 *
 * Default md size: 32×18 track (full radius), 14×14 thumb (white, xs shadow),
 * 2px thumb inset. Off = tNeutral.border200; On = tIndigo.base.
 * 120ms transition. Disabled = 0.5 opacity + not-allowed (per preview —
 * overrides README's general "no opacity" rule; component-specific wins).
 *
 * Simpler than legacy V1 which overlaid SVG check/X icons inside the track.
 *
 * v3 port notes:
 * - Slots renamed from v2's `switchAnatomy`: `container` → `root`,
 *   `track` → `control`. `thumb` keeps its name.
 * - v2 hand-rolled the track/thumb geometry with `cssVar()` from the removed
 *   `@chakra-ui/theme-tools`. v3's built-in recipe ships the same mechanism
 *   (`--switch-width`, `--switch-height`, `--switch-diff`, `--switch-x`), so
 *   the sizes below only set the two input vars and let Chakra derive the
 *   travel distance. Its `--switch-diff` is `width - height`, which is
 *   algebraically identical to v2's `width - thumb - 2*inset` for a thumb
 *   centered with an even inset — and all three sizes below satisfy that, so
 *   the thumb still slides flush from inset-left to inset-right.
 * - Thumb size is derived once as `height - 4px` (a 2px inset on each side)
 *   rather than restated per size. That also keeps it independent of whether
 *   the `variant` or the `size` styles win the merge, which matters because
 *   the built-in `solid` variant pins the thumb to `var(--switch-height)`.
 * - v2 animated the thumb with `transform: translateX(...)`; v3 uses the
 *   `translate` property (see the built-in recipe's `--switch-x`), so the
 *   transition property changes with it.
 * - Chakra's built-in `switch` recipe defaults to `variant: 'solid'` and
 *   deep-merges *under* this one, so `solid` is redefined below to restate the
 *   design-system treatment on exactly the keys it sets. Without that,
 *   `bg.emphasized` (off track), `colorPalette.solid` (on track) and its
 *   `scale: 0.8` thumb all leak through on variant-less call sites.
 */

const trackBase = {
  borderRadius: 'full',
  padding: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  bg: 'tNeutral.border200',
  transitionProperty: 'background-color',
  transitionDuration: '120ms',
  // The standard `shadows.focus` halo (25% indigo) reads weakly against the
  // light `tNeutral.border200` track when the switch is off, especially on
  // white surfaces. `focusStrong` is the same indigo at 40% — see theme.ts.
  focusVisibleRing: 'none',
  _focusVisible: focusRing('strong'),
  _disabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  _checked: {
    bg: 'tIndigo.base',
  },
};

const thumbBase = {
  bg: 'white',
  boxShadow: 'xs',
  borderRadius: 'full',
  width: 'var(--switch-thumb-size)',
  height: 'var(--switch-thumb-size)',
  mx: '2px',
  // Neutralizes the built-in `solid` thumb's `scale: 0.8`, which would shrink
  // the explicitly-sized thumb below the design-system dimensions.
  scale: 1,
  transitionProperty: 'translate',
  transitionDuration: '120ms',
  _checked: {
    bg: 'white',
    translate: 'var(--switch-x) 0',
  },
};

export const switchTheme = defineSlotRecipe({
  slots: ['root', 'control', 'label', 'thumb', 'indicator'],
  base: {
    root: {
      // 2px inset on each side of the track.
      '--switch-thumb-size': 'calc(var(--switch-height) - 4px)',
    },
    control: trackBase,
    thumb: thumbBase,
  },
  variants: {
    variant: {
      dualLabel: {
        // Both labels stay legible, so the track reads indigo in either
        // state and only the thumb position signals the value.
        control: {
          bg: 'tIndigo.base',
          _checked: { bg: 'tIndigo.base' },
        },
      },
      unsaved: {
        control: {
          boxShadow: 'inset 0 0 0 2px {colors.tOrange.base}',
          _checked: {
            boxShadow: 'inset 0 0 0 2px {colors.tOrange.base}',
          },
        },
      },
      // Redefines the built-in `solid` variant — see the header note.
      solid: {
        control: {
          borderRadius: 'full',
          bg: 'tNeutral.border200',
          focusVisibleRing: 'none',
          _checked: { bg: 'tIndigo.base' },
        },
        thumb: {
          bg: 'white',
          width: 'var(--switch-thumb-size)',
          height: 'var(--switch-thumb-size)',
          scale: 1,
          boxShadow: 'xs',
          _checked: { bg: 'white' },
        },
      },
    },
    size: {
      sm: {
        root: {
          '--switch-width': '24px',
          '--switch-height': '14px',
        },
      },
      md: {
        root: {
          '--switch-width': '32px',
          '--switch-height': '18px',
        },
      },
      lg: {
        root: {
          '--switch-width': '40px',
          '--switch-height': '22px',
        },
      },
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

export default switchTheme;
